// Bench Aging — available candidates bucketed by time on bench.
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";

const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : "—");
const daysSince = (d?: string | null) => (d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : null);

function BucketTable({ title, list, variant }: { title: string; list: any[]; variant: "destructive" | "secondary" }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title} ({list.length})</CardTitle></CardHeader>
      <CardContent className="p-0">
        <Table>
          <Thead><Tr><Th>Candidate</Th><Th>Skills</Th><Th>On bench since</Th><Th>Days</Th></Tr></Thead>
          <Tbody>
            {list.length === 0 && <Tr><Td colSpan={4} className="py-6 text-center text-muted-foreground">None.</Td></Tr>}
            {list.map((c: any) => (
              <Tr key={c.id}>
                <Td className="font-medium"><Link to={`/candidates/${c.id}`} className="hover:underline">{c.name}</Link></Td>
                <Td className="text-sm text-muted-foreground">{c.skills?.slice(0, 4).map((s: any) => s.skill).join(", ") || "—"}</Td>
                <Td className="text-sm">{fmtDate(c.benchStartDate)}</Td>
                <Td><Badge variant={variant}>{daysSince(c.benchStartDate) ?? "—"}d</Badge></Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default function BenchAgingPage() {
  const { data, isLoading } = useQuery({ queryKey: ["/candidates/bench/aging"], queryFn: async () => (await api.get("/candidates/bench/aging")).data as any });
  const counts = data?.counts ?? { underThirty: 0, thirtyToSixty: 0, over60: 0 };
  const over60 = data?.over60Alert ?? [];
  const thirty = data?.thirtyToSixtyAlert ?? [];
  return (
    <>
      <PageHeader title="Bench Aging" description="Available candidates by time on bench." />
      <div className="space-y-4 p-6">
        <div className="grid gap-4 md:grid-cols-3">
          <Card><CardContent className="p-4"><div className="text-2xl font-semibold">{counts.underThirty}</div><div className="text-xs text-muted-foreground">Under 30 days</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-2xl font-semibold text-amber-600">{counts.thirtyToSixty}</div><div className="text-xs text-muted-foreground">30–60 days</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-2xl font-semibold text-destructive">{counts.over60}</div><div className="text-xs text-muted-foreground">Over 60 days</div></CardContent></Card>
        </div>
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        <BucketTable title="Over 60 days" list={over60} variant="destructive" />
        <BucketTable title="30–60 days" list={thirty} variant="secondary" />
      </div>
    </>
  );
}
